/**
 * Выбор объекта в разделах «Объекты» и «Шкафы».
 *
 * Список один и тот же, различается только то, что происходит при
 * выборе. Объекты собраны по дивизионам, как на дашборде; полоска под
 * названием — та же, что на плитках дашборда: без замечаний, обработано,
 * требует внимания.
 *
 * На компьютере и планшете — колонка слева. На телефоне колонки нет:
 *   · объект не выбран — плитки объектов занимают весь экран;
 *   · объект выбран — другой выбирается из панели снизу, она открывается
 *     нажатием на название объекта в шапке (ObjectTitle).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectGrouped, selectPrefixMap } from '../../store/slices/switchesSlice';
import { selectMagToDivision } from '../../store/slices/divisionsSlice';
import { closeMobileSidebar } from '../../store/slices/uiSlice';
import { objectLabel } from '../../domain/issues';
import { getObjectHealth } from '../../domain/dashboard';
import ObjectPicker from './ObjectPicker';
import styles from './ObjectSidebar.module.css';

/* Недавние объекты — удобство одного человека в одном браузере.
   Хранилище может быть недоступно (приватный режим) — тогда без них. */
const RECENT_KEY = 'sw-recent-objects';
const RECENT_MAX = 4;

function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((m) => typeof m === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function writeRecent(list) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* без недавних */ }
}

const compact = (s) => s.toLowerCase().replace(/\s+/g, '');

export default function ObjectSidebar({
  selectedMag, onSelect, title = 'Объекты', homeTitle = title, homeHint, children,
}) {
  const dispatch = useDispatch();
  const grouped = useSelector(selectGrouped);
  const prefixMap = useSelector(selectPrefixMap);
  const magToDivision = useSelector(selectMagToDivision);
  const statuses = useSelector((state) => state.collab.statuses);
  const isOpen = useSelector((state) => state.ui.mobileSidebarOpen);

  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState(readRecent);
  const sheetRef = useRef(null);

  /* Все объекты с состоянием — общая основа и для колонки, и для плиток. */
  const divisions = useMemo(() => {
    const isProcessed = (mag, sw) => Boolean(statuses[`${mag}__${sw}`]);
    return getObjectHealth(grouped, isProcessed, magToDivision).map((division) => ({
      ...division,
      objects: division.objects.map((o) => ({ ...o, label: objectLabel(o.mag, prefixMap) })),
    }));
  }, [grouped, prefixMap, magToDivision, statuses]);

  const groups = useMemo(() => {
    const search = compact(query.trim());
    if (!search) return divisions;
    return divisions
      .map((d) => ({ ...d, objects: d.objects.filter((o) => compact(o.label).includes(search) || o.mag.includes(search)) }))
      .filter((d) => d.objects.length > 0);
  }, [divisions, query]);

  const total = Object.keys(grouped).length;
  const shown = groups.reduce((n, d) => n + d.objects.length, 0);
  const nothingSelected = !selectedMag || !grouped[selectedMag];
  const sheetOpen = isOpen && !nothingSelected;

  /* Открытый объект — в недавние, откуда бы его ни открыли: из списка,
     из поиска по сети, по ссылке. */
  useEffect(() => {
    if (nothingSelected) return;
    setRecent((prev) => {
      if (prev[0] === selectedMag) return prev;
      const next = [selectedMag, ...prev.filter((m) => m !== selectedMag)].slice(0, RECENT_MAX);
      writeRecent(next);
      return next;
    });
  }, [selectedMag, nothingSelected]);

  /* Панель снизу: Esc закрывает, фокус переходит в панель. */
  useEffect(() => {
    if (!sheetOpen) return undefined;
    sheetRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') dispatch(closeMobileSidebar()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen, dispatch]);

  function handleSelect(mag) {
    onSelect(mag);
    dispatch(closeMobileSidebar());
  }

  return (
    <div className={`${styles.content} ${nothingSelected ? styles.contentHome : ''}`}>
      <aside className={styles.sidebar} aria-label="Список объектов">
        <div className={styles.head}>
          <div className={styles.title}>
            {title}
            <span className={styles.total}>{query ? `${shown} из ${total}` : total}</span>
          </div>
          <input
            className={styles.searchInput}
            type="search"
            placeholder="Номер объекта"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Найти объект"
          />
        </div>

        <div className={styles.list}>
          {groups.length === 0 && <div className={styles.none}>Объект не найден</div>}
          {groups.map((division) => (
            <div key={division.name} className={styles.group}>
              <div className={styles.groupName}>{division.name}</div>
              {division.objects.map((o) => (
                <button
                  key={o.mag}
                  className={`${styles.item} ${selectedMag === o.mag ? styles.itemActive : ''}`}
                  onClick={() => handleSelect(o.mag)}
                  aria-current={selectedMag === o.mag ? 'page' : undefined}
                  title={`${o.label}: без замечаний ${o.clean}, обработано ${o.checked}, требуют внимания ${o.open}`}
                >
                  <span className={styles.itemName}>{o.label}</span>
                  <span className={styles.count}>{o.total}</span>
                  <span className={styles.strip} aria-hidden="true">
                    {o.clean > 0 && <span className={styles.segClean} style={{ flexGrow: o.clean }} />}
                    {o.checked > 0 && <span className={styles.segChecked} style={{ flexGrow: o.checked }} />}
                    {o.open > 0 && <span className={styles.segOpen} style={{ flexGrow: o.open }} />}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </aside>

      {/* Телефон, объект не выбран: плитки — это и есть страница */}
      {nothingSelected && (
        <div className={styles.mobileHome}>
          <ObjectPicker
            divisions={divisions}
            recent={recent}
            title={homeTitle}
            hint={homeHint}
            onSelect={handleSelect}
          />
        </div>
      )}

      <div className={styles.panel}>{children}</div>

      {/* Телефон, объект выбран: другой — из панели снизу */}
      {sheetOpen && (
        <div className={styles.sheetLayer}>
          <button
            type="button"
            className={styles.scrim}
            onClick={() => dispatch(closeMobileSidebar())}
            aria-label="Закрыть выбор объекта"
            tabIndex={-1}
          />
          <div
            ref={sheetRef}
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label="Выбор объекта"
            tabIndex={-1}
          >
            <span className={styles.grabber} aria-hidden="true" />
            <ObjectPicker
              divisions={divisions}
              recent={recent.filter((m) => m !== selectedMag)}
              selectedMag={selectedMag}
              title="Другой объект"
              onSelect={handleSelect}
              onClose={() => dispatch(closeMobileSidebar())}
            />
          </div>
        </div>
      )}
    </div>
  );
}
